import { fetchSampleFile } from "@/lib/sampleFile";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import {
  readActiveOrgSlugFromHost,
  useIsCurrentOrgAdmin,
} from "@/api/queries/orgs";
import { useTenantBranding } from "@/api/queries/branding";
import { ShopNotOrderableNotice } from "@/components/ShopNotOrderableNotice";
import { useProcesses } from "@/api/queries/processes";
import { useUploadQuote, useUploadQuoteBatch } from "@/api/queries";
import { ContactForm } from "@/components/home/ContactForm";
import { EstimateCard } from "@/components/home/EstimateCard";
import { buildFeaturesHeading, Features } from "@/components/home/Features";
import { FinalCta } from "@/components/home/FinalCta";
import { buildHighlights } from "@/components/home/highlights";
import { HomeFaq, useHasHomeFaq } from "@/components/home/HomeFaq";
import { ThreeStepRow } from "@/components/home/ThreeStepRow";
import { TrustRow } from "@/components/home/TrustRow";
import {
  ProcessPicker,
  readLastModuleSlug,
  rememberLastModuleSlug,
} from "@/components/ProcessPicker";
import { ProcessSwitch } from "@/components/ProcessSwitch";
import { UploadDropzone } from "@/components/UploadDropzone";
import { PageHero } from "@/components/ui/PageHero";
import { ShellBanner } from "@/components/ui/ShellBanner";
import { Pill } from "@/components/ui/Pill";
import { requestEmbedBreakout } from "@/embed/protocol";
import { DEFAULT_LANG, isSupportedLang } from "@/i18n";
import { useSeo } from "@/lib/seo";
import { usePageBlocks } from "@/shell/usePageBlocks";
import { formatUploadError } from "@/lib/uploadErrors";
import { UploadErrorAdminInsight } from "@/components/UploadErrorAdminInsight";
import { formatCzk } from "@/lib/utils";
import {
  findModuleForFilename,
  hasConfigurator,
  sortByLandingOrder,
} from "@/processes/registry";
import type { Process, Quote } from "@/api/types";

function extOf(name: string): string {
  return name.includes(".")
    ? name.slice(name.lastIndexOf(".")).toLowerCase()
    : "";
}

function acceptsExt(p: Process, ext: string): boolean {
  return (
    ext !== "" &&
    p.accepted_file_types.some((accepted) => accepted.toLowerCase() === ext)
  );
}

interface LandingProps {
  /** When rendered as a per-service page (`/services/{slug}`), pass the
   *  picked process so the hero scopes to that module and the picker
   *  grid disappears (it's redundant after the user has picked). */
  process?: Process;
  /** Rendered inside the embeddable configurator (`/embed`).
   *
   *  Two differences, both because the page is then a fragment of
   *  somebody else's website rather than a page of ours:
   *  - the marketing sections below the hero are dropped;
   *  - "continue" leaves the iframe for the tenant's own storefront
   *    instead of navigating inside the frame. See
   *    ``docs/specs/spec_embed_widget.md`` §E1b. */
  embedded?: boolean;
}

function LandingSetupRequired(): JSX.Element {
  const { t } = useTranslation("common");

  return (
    <div
      className="rounded-2xl border-2 border-dashed border-warn-border bg-warn-bg px-6 py-10 text-center sm:px-10 sm:py-12"
      data-testid="landing-setup-required"
    >
      <div
        aria-hidden="true"
        className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-surface text-warn shadow-sm"
      >
        <svg
          className="h-7 w-7"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.75}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M11.42 15.17 17.25 21A2.652 2.652 0 0 0 21 17.25l-5.83-5.83M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 1 1-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 0 0 4.486-6.336l-3.276 3.277a3.004 3.004 0 0 1-2.25-2.25l3.276-3.276a4.5 4.5 0 0 0-6.336 4.486c.091 1.076-.071 2.264-.904 2.95l-.102.085"
          />
        </svg>
      </div>
      <h3 className="mt-5 text-lg font-semibold text-ink sm:text-xl">
        {t("home.hero.setupRequiredTitle")}
      </h3>
      <p className="mx-auto mt-2 max-w-2xl text-sm leading-6 text-ink-2">
        {t("home.hero.setupRequiredBody")}
      </p>
    </div>
  );
}

// Placeholder rendered while ``useTenantBranding`` is still in flight.
// Footprint mirrors ``UploadDropzone`` (rounded-2xl, dashed border, same
// padding, icon + headline + hint + button row + divider + 3 pills) so
// the real dropzone appears in place without a layout shift. Neutral
// slate palette deliberately avoids the amber empty-state look — a
// loading state must not be mistakable for "not configured".
function LandingDropzoneSkeleton(): JSX.Element {
  return (
    <div
      aria-busy="true"
      aria-live="polite"
      data-testid="landing-upload-loading"
      className="animate-pulse rounded-2xl border-2 border-dashed border-border-strong bg-surface px-6 py-10 text-center sm:px-10 sm:py-12"
    >
      <span className="sr-only">Načítám…</span>
      <div aria-hidden="true" className="mx-auto h-14 w-14 rounded-2xl bg-surface-3" />
      <div aria-hidden="true" className="mx-auto mt-5 h-6 w-64 max-w-full rounded-md bg-surface-3 sm:h-7" />
      <div aria-hidden="true" className="mx-auto mt-3 h-4 w-80 max-w-full rounded bg-surface-2" />
      <div aria-hidden="true" className="mt-6 flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:items-center">
        <div className="h-11 w-full rounded-md bg-surface-3 sm:w-44" />
        <div className="h-11 w-full rounded-md bg-surface-2 sm:w-44" />
      </div>
      <hr className="mx-auto mt-8 max-w-xl border-t border-dashed border-border" />
      <div aria-hidden="true" className="mt-5 flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
        <div className="h-3 w-20 rounded bg-surface-2" />
        <div className="h-3 w-16 rounded bg-surface-2" />
        <div className="h-3 w-24 rounded bg-surface-2" />
      </div>
    </div>
  );
}

export default function Landing({
  process,
  embedded = false,
}: LandingProps = {}): JSX.Element {
  const { t, i18n } = useTranslation();
  const { t: tQuote } = useTranslation("quote");
  const { t: tCommon } = useTranslation("common");
  const { lang: langParam } = useParams<{ lang: string }>();
  // The embed is mounted at ``/embed``, OUTSIDE the ``/:lang`` tree
  // (``router.tsx``), so the route param is always undefined there.
  // Falling straight through to ``DEFAULT_LANG`` pinned every breakout
  // target to ``/cs/…`` — an English widget handed the visitor to a
  // Czech configurator, cart, checkout, terms and order e-mail
  // (KAN-505). Inside the frame the language is the one ``EmbedPage``
  // put on i18next from ``?lang=``, which is what the host page asked
  // for in ``data-lang``.
  const lang = isSupportedLang(langParam)
    ? langParam
    : embedded && isSupportedLang(i18n.language)
      ? i18n.language
      : DEFAULT_LANG;
  const routerNavigate = useNavigate();
  // Inside the embed a router navigation would swap the iframe's
  // contents for a chrome-less page on somebody else's site. Hand the
  // visitor to the tenant's own storefront instead — same destination,
  // but in their address bar, where they can see whose shop they are
  // in before they type anything personal.
  const navigate = embedded ? requestEmbedBreakout : routerNavigate;

  // ``landing_facts`` is null on the platform default tenant — the
  // homepage marketing site keeps no per-tenant config so the row
  // collapses to nothing rather than rendering placeholder copy.
  const branding = useTenantBranding();
  const facts = branding.data?.landing_facts;
  const canUpload = facts != null;
  // Čte se jen z cache ``auth.me``, kterou hlavička načítá na každé
  // stránce — anonymní návštěvník tím nezdraží ani o jeden request.
  // Používá se výhradně v proužku pro vlastníka níž.
  const { isAdmin: isTenantAdmin } = useIsCurrentOrgAdmin();
  const hasFaq = useHasHomeFaq();
  // Blocks the tenant switched off (KAN-1635); the per-service pages
  // render this same landing, so the switches apply there too.
  const showBlock = usePageBlocks("home");

  const heroTitle = process?.display_name ?? t("landing.title");
  // Hero subtitle: dynamic variant only when the tenant has at least
  // one public-entry module with a calc-time SLA declared
  // (``landing_facts.sla_calc_seconds``). Otherwise fall back to a
  // generic subtitle without a numeric time claim — per the project
  // rule that marketing time/delivery promises must come from BE
  // landing_facts, never hardcoded i18n.
  const heroSubtitle =
    process?.description ??
    (facts?.sla_calc_seconds != null
      ? t("landing.subtitleDynamic", {
          seconds: facts.sla_calc_seconds,
          defaultValue: t("landing.subtitle"),
        })
      : t("landing.subtitle"));
  // On a per-service page (``/services/{slug}``) the eyebrow badge
  // advertises the order subtotal floor — "od X Kč" — pulled from the
  // tenant admin Order Limits via ``landing_facts.min_subtotal_czk``.
  // When the tenant has no floor configured the badge falls back to
  // the module's neutral ``description`` (kept free of numeric promises
  // so a stale value can't contradict checkout). The homepage shell
  // keeps the generic marketing eyebrow regardless.
  const minSubtotalCzk = facts?.min_subtotal_czk ?? null;
  const minSubtotalBadge =
    minSubtotalCzk != null && minSubtotalCzk > 0
      ? `${tCommon("home.hero.fromPrefix")} ${formatCzk(minSubtotalCzk)}`
      : null;
  const heroBadge = process
    ? minSubtotalBadge ?? process.description
    : tCommon("home.hero.eyebrowBadge");

  useSeo({
    title: `${heroTitle} · ${t("app.name")}`,
    description: heroSubtitle,
    path: process ? `services/${process.slug}` : "",
    lang,
  });

  const processesQuery = useProcesses();
  // „Hotový odhad" — when the process authors an estimate_summary_schema, the
  // homepage stays put after the analysis and shows the estimate card instead
  // of navigating straight to the configurator. Processes without the schema
  // keep the instant redirect.
  const [estimateQuote, setEstimateQuote] = useState<Quote | null>(null);
  const upload = useUploadQuote({
    onSuccess: (quote) => {
      if (!process && quote.estimate_summary) {
        setEstimateQuote(quote);
        return;
      }
      navigate(`/${lang}/quote/${quote.id}`);
    },
  });
  const batch = useUploadQuoteBatch({
    onSuccess: (res) => navigate(`/${lang}/quote-batch/${res.public_id}`),
  });

  const pending = upload.isPending || batch.isPending;
  const errorMessage = formatUploadError(
    batch.error ?? upload.error,
    tQuote,
    facts?.max_upload_mb,
  );

  // Smart-routing fallback: if a dropped file matches a module
  // *other than* the tenant's primary public-entry process,
  // redirect to that module's per-service landing instead of
  // submitting through the homepage's default pipeline. Replaces
  // the legacy named-process hardcode so every tenant routes the
  // default upload to whatever their primary
  // file-driven module is (``landing_facts.primary_public_entry_slug``).
  const primarySlug = facts?.primary_public_entry_slug ?? null;

  // ── Segmentový přepínač procesu („Volba procesu V1") ─────────────────
  // Step 1 of the hero: the visitor picks WHAT they make, the dropzone
  // below scopes to that process. Only file-driven, currently-available
  // processes with a working configurator and at most one REQUIRED file
  // slot qualify — the hero dropzone posts a single file, so a
  // multi-required-file process keeps its per-service page (still
  // reachable via the picker grid below the hero).
  const switchProcesses = useMemo(
    () =>
      sortByLandingOrder(
        (processesQuery.data ?? []).filter(
          (p) =>
            p.is_available &&
            p.requires_file &&
            hasConfigurator(p) &&
            (p.file_inputs ?? []).filter((fi) => fi.required).length <= 1,
        ),
      ),
    [processesQuery.data],
  );
  // Initial selection: last-used module (shared ``ps.lastModule`` key
  // with the picker grid) → tenant's primary public entry → first by
  // ``landing_order``.
  const [selectedSlug, setSelectedSlug] = useState<string | null>(() =>
    readLastModuleSlug(),
  );
  // Auto-switch toast ("Přepnuto na 3D tisk podle přípony .stl").
  const [autoSwitch, setAutoSwitch] = useState<{
    toSlug: string;
    ext: string;
  } | null>(null);
  const selectedProcess =
    switchProcesses.find((p) => p.slug === selectedSlug) ??
    switchProcesses.find((p) => p.slug === primarySlug) ??
    switchProcesses[0] ??
    null;
  const showSwitch = !process && selectedProcess != null;
  // The per-process scoping (dropzone headline, hint, chips, sample) only
  // kicks in when there's an actual choice; a single-process tenant keeps
  // the generic copy and just gets a static process label.
  const scopedProcess = showSwitch && switchProcesses.length > 1 ? selectedProcess : null;
  const autoSwitchTarget = autoSwitch
    ? switchProcesses.find((p) => p.slug === autoSwitch.toSlug) ?? null
    : null;
  // Multi-operation hint ("Díl potřebuje víc operací? …") — shown only when
  // the tenant actually offers chain operations: modules present in the
  // chain-inclusive registry but absent from the default (primary) list.
  // Fully tenant-driven — a single-process shop never sees the line.
  const chainProcessesQuery = useProcesses({ includeNonEntry: true });
  const hasChainOps = useMemo(() => {
    const primaries = new Set((processesQuery.data ?? []).map((p) => p.name));
    return (chainProcessesQuery.data ?? []).some(
      (p) => !primaries.has(p.name) && p.is_available,
    );
  }, [processesQuery.data, chainProcessesQuery.data]);
  const onSwitchProcess = (slug: string) => {
    setSelectedSlug(slug);
    setAutoSwitch(null);
    setEstimateQuote(null);
    rememberLastModuleSlug(slug);
  };
  // Bez tohohle posíláme upload bez ``process_type`` a BE router
  // ([routers/quotes.py]) defaultuje na default-primary modul → STL z 3D-print
  // tenantu skončí v DXF parseru a vyhodí "Failed to read DXF file".
  const resolveProcessType = (
    target: { name: string } | null,
  ): string | undefined => {
    // G6 — pass the resolved process_type through (any registered slug) so
    // a new file-based process is analyzed by ITS analyzer rather than silently
    // hardcoded default. ``undefined`` only when nothing resolves (BE → default-primary).
    const name =
      target?.name ??
      process?.name ??
      processesQuery.data?.find((p) => p.slug === primarySlug)?.name;
    return name ? String(name) : undefined;
  };
  const onFilesChosen = (files: File[]) => {
    setAutoSwitch(null);
    if (files.length === 1) {
      // Switch-first resolution (homepage): the selected process decides
      // the analyzer. When the extension belongs to a DIFFERENT switch
      // process, softly switch + tell the user via the toast ("formát
      // jako pojistka") — the file still uploads in one gesture.
      if (!process && selectedProcess) {
        const ext = extOf(files[0].name);
        if (acceptsExt(selectedProcess, ext)) {
          upload.mutate({
            file: files[0],
            lang,
            processType: String(selectedProcess.name),
          });
          return;
        }
        const candidates = switchProcesses.filter((p) => acceptsExt(p, ext));
        if (candidates.length > 0) {
          const target = candidates[0];
          onSwitchProcess(target.slug);
          setAutoSwitch({ toSlug: target.slug, ext });
          upload.mutate({
            file: files[0],
            lang,
            processType: String(target.name),
          });
          return;
        }
        // Fall through — legacy smart-routing below may still know a
        // module outside the switch (multi-required-file processes).
      }
      const target = findModuleForFilename(processesQuery.data, files[0].name);
      if (target && primarySlug && target.slug !== primarySlug) {
        if (!target.is_available) {
          // Reuse the upload error surface to tell the user the
          // detected module isn't available yet — the dropzone
          // shows the message inline.
          upload.reset();
          batch.reset();
          return;
        }
        navigate(`/${lang}/services/${target.slug}`);
        return;
      }
      upload.mutate({
        file: files[0],
        lang,
        processType: resolveProcessType(target),
      });
      return;
    }
    // Batch endpoint (``POST /quotes/batch``) dnes bere jen soubory —
    // ``UploadQuoteBatchVars`` ``processType`` nepřijímá a BE resolvuje
    // default-primary modul. Multi-STL drop na 3D-print tenantu je
    // out-of-scope tohoto fixu (batch orchestrator pro mesh zatím
    // neexistuje).
    batch.mutate({ files, lang });
  };

  // Sample CTA follows the switch selection ("ukázka respektuje volbu")
  // — the per-process ``sample_file_url`` carries the platform default
  // or the tenant's override. Tenants without the switch keep the
  // legacy landing-facts sample.
  const sampleFileUrl =
    (showSwitch ? selectedProcess?.sample_file_url : facts?.sample_file_url) ??
    null;
  const onSampleFile = async () => {
    if (pending || !sampleFileUrl) return;
    // Selhání je tiché — zákazník může nahrát vlastní soubor.
    const file = await fetchSampleFile(sampleFileUrl);
    if (file) onFilesChosen([file]);
  };

  const features = buildHighlights(facts, tCommon, "form", i18n.language);
  // Format chips reflect what the tenant's enabled processes
  // actually accept (union across modules). The chip row is
  // suppressed on the platform default shell (``landing_facts``
  // null) and on per-service pages (the picker block above already
  // disappears, and the chips are tucked under the upload form
  // which isn't rendered for ``process`` either).
  const unionFormats: string[] = facts?.accepted_file_formats ?? [];
  // Per-tenant upload limits, sourced from the module registry via
  // ``compute_landing_facts``. ``UploadDropzone`` consumes the
  // ``acceptedFileTypes`` array as both the ``<input accept>`` and
  // the client-side extension gate; ``maxBytes`` becomes the
  // per-file size cap (max across enabled modules so .stl drops on
  // a two-process tenant aren't rejected by the stricter cap).
  //
  // The gate deliberately stays the tenant-wide UNION even when the
  // process switch is active — a drop of another process's file must
  // pass validation to reach the auto-switch resolution in
  // ``onFilesChosen``. Only the visible copy (chips + dropzone hint)
  // scopes to the selected process.
  const acceptedFileTypes = unionFormats.length
    ? unionFormats.map((fmt) => `.${fmt.toLowerCase()}`)
    : undefined;
  const formatChips: string[] = scopedProcess
    ? scopedProcess.accepted_file_types.map((ext) =>
        ext.replace(/^\./, "").toUpperCase(),
      )
    : unionFormats;
  const maxBytes = facts?.max_upload_mb
    ? facts.max_upload_mb * 1024 * 1024
    : undefined;

  // Skryjeme oba pod-hero řádky (formát-chipy + feature checkmarks),
  // když tenant nabízí jen jednu službu (nebo žádnou). U
  // jednoslužbového tenanta vypadá "1 podporovaný materiál"
  // spíš jako handicap než feature a ``ServicesDropdown`` už pro
  // tenhle případ degraduje menu na přímý link — sjednocujeme
  // chování v hero ribbon. Predikát kopíruje filtr dropdownu
  // (``hasConfigurator`` nad výchozím seznamem, který UŽ JE landing sada).
  const landingServiceCount = (processesQuery.data ?? []).filter((p) =>
    hasConfigurator(p),
  ).length;
  const isSingleService = landingServiceCount <= 1;

  // Vlastníkův proužek "obchod ještě není spuštěný". Na tenantní
  // subdoméně (``<slug>.forgmatic.com``), jejíž
  // ``shop_readiness.checkout_ready`` je false — chybí doprava, termíny,
  // kurz nebo naceněné materiály — dostane PŘIHLÁŠENÝ VLASTNÍK nad
  // stránkou zkratku do průvodce. Návštěvník nevidí nic navíc.
  //
  // Do KAN-1040 tady místo proužku stál placeholder, který celou
  // domovskou stránku ZAHODIL. Byla to půlka brány: podmínka měla
  // ``!process``, takže ``/3d-tisk`` i ostatní veřejné stránky se
  // rendrovaly dál včetně nahrávacího pole a cesty do košíku — schoval
  // se jen index. Brána proti "obchod předstírá, že bere objednávky"
  // žije o patro níž v ``Checkout`` (týž ``checkout_ready`` blokuje
  // odeslání objednávky), takže placeholder nechránil nic navíc.
  //
  // V embedu se proužek nerendruje: ten běží na cizím webu a
  // provozní hláška pro vlastníka tam nepatří.
  const slugFromHost = readActiveOrgSlugFromHost();
  const checkoutReady =
    branding.data?.shop_readiness?.checkout_ready ?? false;
  const showOwnerSetupBanner =
    !process &&
    !embedded &&
    slugFromHost !== null &&
    branding.isSuccess &&
    !checkoutReady &&
    isTenantAdmin;

  return (
    <>
      {/* KAN-2096 — návštěvník (ne vlastník, ten má proužek výš) se
          o nedokončitelné objednávce dozví tady, ne až v pokladně. */}
      {slugFromHost !== null && !showOwnerSetupBanner && (
        <ShopNotOrderableNotice />
      )}
      {showOwnerSetupBanner && (
        <ShellBanner
          tone="warn"
          testId="landing-owner-setup"
          action={{
            to: `/${lang}/admin/onboarding`,
            label: tCommon("publicTenant.unsetupOwnerCta"),
          }}
        >
          {tCommon("publicTenant.unsetupOwnerBody")}
        </ShellBanner>
      )}
      <PageHero
        bordered={false}
        testId={process ? `service-hero-${process.slug}` : "home-hero"}
        innerClassName="st-hero max-w-4xl px-7 pb-16 pt-12 text-center md:pt-20"
      >
        {/* The st-hero-* groups: a storefront theme re-arranges them for
            its hero variant (KAN-1547, shell/siteThemes.css). */}
        <div className="st-hero-copy">
        <Pill tone="accent" className="st-pill mx-auto mb-5">
          {heroBadge}
        </Pill>
        <h2
          data-testid="hero-heading"
          className="st-h1 text-balance text-4xl font-semibold tracking-tighter text-ink md:text-6xl"
        >
          {process ? (
            heroTitle
          ) : (
            <>
              {t("landing.titleLead")}{" "}
              <span className="text-ink-3">{t("landing.titleTail")}</span>
            </>
          )}
        </h2>
        <p className="st-sub mx-auto mt-4 max-w-2xl text-balance text-lg text-ink-2">
          {heroSubtitle}
        </p>
        </div>
        <div className="st-hero-act mt-10">
          {/* Step 1 — segmented process switch ("Volba procesu V1").
              Multi-process tenants pick the technology here and the
              dropzone below scopes to it; a single process renders as
              a static label (zero extra decisions). Hidden until the
              branding gate confirms the tenant can upload at all. */}
          {showSwitch && selectedProcess && canUpload && (
            <div
              className="mb-5 flex flex-col items-center justify-center gap-3 sm:flex-row"
              data-testid="process-switch-row"
            >
              {switchProcesses.length > 1 && (
                <span className="text-sm font-medium text-ink-3">
                  {tCommon("home.processSwitch.label")}
                </span>
              )}
              <ProcessSwitch
                processes={switchProcesses}
                value={selectedProcess.slug}
                onChange={onSwitchProcess}
                disabled={pending}
              />
            </div>
          )}
          {/* Three-way gate: skeleton while ``useTenantBranding`` is in
              flight, dropzone once we know the tenant has a public
              offer, amber empty-state only when the load completed
              (success OR error) and there's nothing to upload into.
              Without the ``isPending`` guard the empty-state flashes
              for ~300–700 ms on every cold landing because
              ``data === undefined`` collapses to ``canUpload === false``. */}
          {branding.isPending ? (
            <LandingDropzoneSkeleton />
          ) : estimateQuote ? (
            <EstimateCard
              quote={estimateQuote}
              process={
                processesQuery.data?.find(
                  (p) => p.name === estimateQuote.process_type,
                ) ?? null
              }
              allProcesses={chainProcessesQuery.data}
              lang={lang}
              onOpenConfigurator={() =>
                navigate(`/${lang}/quote/${estimateQuote.id}`)
              }
              onReset={() => setEstimateQuote(null)}
            />
          ) : canUpload ? (
            <UploadDropzone
              disabled={pending}
              onFilesChosen={onFilesChosen}
              errorMessage={errorMessage}
              statusLabel={pending ? tQuote("upload.analyzing") : null}
              onSampleFile={sampleFileUrl ? onSampleFile : undefined}
              acceptedFileTypes={acceptedFileTypes}
              title={
                scopedProcess
                  ? tQuote("upload.titleForProcess", {
                      process: scopedProcess.display_name,
                    })
                  : null
              }
              hintFileTypes={scopedProcess?.accepted_file_types}
              maxBytes={maxBytes}
              slaCalcSeconds={facts.sla_calc_seconds ?? null}
            />
          ) : (
            <LandingSetupRequired />
          )}
          {/* KAN-1923 — pokyn vlastníkovi k chybě, kterou způsobilo
              nastavení obchodu; zákazník vidí jen větu v dropzone. */}
          {!embedded && (
            <UploadErrorAdminInsight
              error={batch.error ?? upload.error}
              lang={lang}
            />
          )}
          {/* Multi-operation hint — only when the tenant offers chain
              operations (chain ops) beyond the primary
              processes; the actual chain gets picked in the
              configurator after upload (or auto-suggested by the
              tenant's chain_recommendation rules). */}
          {!process && canUpload && !estimateQuote && hasChainOps && (
            <p
              className="mt-4 text-center text-xs text-ink-4"
              data-testid="landing-multi-op-hint"
            >
              {tCommon("home.processSwitch.multiOpHint")}
            </p>
          )}
          {/* Auto-switch toast — the drop's extension unambiguously
              belonged to another process, so the switch moved there
              before uploading. Dismiss-only: the switch itself is one
              click away if the visitor wants a different process for
              the NEXT upload. */}
          {autoSwitchTarget && (
            <div aria-live="polite" className="mt-4 flex justify-center">
              <span
                className="inline-flex items-center gap-2 rounded-full bg-ink py-1.5 pl-4 pr-1.5 text-xs font-medium text-bg shadow-lg"
                data-testid="process-auto-switch-toast"
              >
                {tCommon("home.processSwitch.autoSwitched", {
                  process: autoSwitchTarget.display_name,
                  ext: autoSwitch?.ext,
                })}
                <button
                  type="button"
                  onClick={() => setAutoSwitch(null)}
                  aria-label={tCommon("home.processSwitch.dismiss")}
                  className="flex h-6 w-6 items-center justify-center rounded-full text-white/70 hover:bg-white/10 hover:text-white"
                >
                  ×
                </button>
              </span>
            </div>
          )}
        </div>

        {!process && !isSingleService && (
          <div className="st-hero-extra">
            {formatChips.length > 0 && (
              <ul
                aria-label={tCommon("home.hero.formatsLabel")}
                className="mt-6 flex flex-wrap items-center justify-center gap-2"
                data-testid="landing-format-chips"
              >
                {formatChips.map((fmt) => (
                  <li
                    key={fmt}
                    className="rounded-full border border-border bg-surface px-3 py-1 text-xs font-medium tracking-wide text-ink-2"
                  >
                    {fmt}
                  </li>
                ))}
              </ul>
            )}

            {features.length > 0 && (
              <ul
                aria-label={tCommon("home.hero.featuresLabel")}
                className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-ink-2"
                data-testid="landing-feature-list"
              >
                {features.map((feature) => (
                  <li key={feature.key} className="flex items-center gap-1.5">
                    <svg
                      aria-hidden="true"
                      className="h-4 w-4 text-accent"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                    {feature.label}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </PageHero>

      {/* Everything below the hero is marketing: it sells the shop.
          Inside the embed the shop has already been chosen — the
          visitor is on the tenant's own website — so the whole block
          drops and only the quoting surface remains. One wrapper
          rather than a flag per section: the sections are contiguous
          and adding one must not mean remembering a seventh
          conditional. */}
      {!embedded && (
      <>
      {/* Process picker — registry-driven grid that lets the user
          choose what to make. Available modules link to their service
          landing; unavailable ones render a Brzy badge for marketing
          + SEO purposes. Lives below the hero so the existing
          drop-DXF flow stays the path of least resistance for
          returning customers.

          Hidden on per-service pages (``/services/{slug}``) — the
          customer has already picked, so re-rendering the grid would
          make the page look identical to the homepage.

          Also hidden when the tenant has ≤ 1 registered process —
          a picker with one option is just visual noise. */}
      {showBlock("process_picker") && !process && (processesQuery.data?.length ?? 0) > 1 && (
        <section className="mx-auto max-w-6xl px-7 pb-16" data-testid="landing-process-picker-section">
          <h2 className="mb-6 text-2xl font-semibold tracking-tight text-ink">
            {tCommon("home.processPicker.heading", { defaultValue: "Co chcete vyrobit?" })}
          </h2>
          <ProcessPicker lang={lang} />
        </section>
      )}

      {showBlock("trust") && (
      <section className="mx-auto max-w-6xl px-7 pb-16">
        <TrustRow />
      </section>
      )}

      {/* Three-step summary */}
      {showBlock("steps") && (
      <section className="st-steps-sec st-sec-alt border-t border-border bg-surface">
        <div className="st-sec-in mx-auto max-w-6xl px-7 py-20">
          <div className="st-sec-head">
          <h2 className="st-h2 max-w-2xl text-3xl font-semibold tracking-tight text-ink">
            {tCommon("home.threeStep.heading")}
          </h2>
          <p className="mt-2 max-w-2xl text-ink-2">
            {tCommon("home.threeStep.lead")}
          </p>
          </div>
          <div className="st-sec-body mt-8">
            <ThreeStepRow />
          </div>
        </div>
      </section>
      )}

      {/* Features — heading is composed from landing_facts.delivery_tiers
          so the page never claims a delivery window that isn't in the
          tenant's capacity-delivery-tiers admin. The lead is a generic
          "what you get" line; both keys are in LOCKED_I18N_KEYS so a
          tenant cannot override them to unbacked claims. */}
      {showBlock("benefits") && (
      <section className="st-ben-sec border-t border-border bg-bg">
        <div className="st-sec-in mx-auto max-w-6xl px-7 py-20">
          <div className="st-sec-head">
          <h2 className="st-h2 max-w-2xl text-3xl font-semibold tracking-tight text-ink">
            {buildFeaturesHeading(
              tCommon,
              branding.data?.landing_facts?.delivery_tiers ?? [],
            )}
          </h2>
          <p className="mt-2 max-w-2xl text-ink-2">
            {tCommon("home.features.lead")}
          </p>
          </div>
          <div className="st-sec-body mt-10">
            <Features />
          </div>
        </div>
      </section>
      )}

      {/* FAQ — the whole section (heading included) hides when the
          tenant has FAQ enabled on no process. */}
      {showBlock("faq") && hasFaq && (
        <section className="border-t border-border bg-bg">
          <div className="st-sec-in mx-auto max-w-3xl px-7 py-20">
            <h2 className="st-h2 mb-6 text-3xl font-semibold tracking-tight text-ink">
              {tCommon("home.faq.heading")}
            </h2>
            <HomeFaq />
          </div>
        </section>
      )}

      {/* Contact form — posts to ``/api/v1/contact``. When rendered
          on a tenant landing the slug travels with the submission so
          it lands on the right Kanban inbox; the platform homepage
          omits the slug so the message goes to the platform inbox. */}
      {showBlock("contact_form") && (
        <ContactForm orgSlug={branding.data?.slug ?? undefined} />
      )}

      {showBlock("final_cta") && branding.isSuccess && canUpload && <FinalCta />}
      </>
      )}
    </>
  );
}

